import { c as _c } from "react/compiler-runtime";
import type { HofContact } from '@/features/hof-dashboard/api/hof-dashboard-data';
import { HOF_ENTRY_STATUS_LABELS, type HofDashboardArea } from '@/features/hof-dashboard/constants';
import { formatCountdown, formatDate, translate } from '@/features/hof-dashboard/texts';
import { isOpen, type SubmissionProgress, type SubmissionState } from '@/features/hof-dashboard/utils/submission-progress';
import type { Locale } from '@/types/types';
import { cn } from '@/utils/tailwindcss-override';
import { AlertCircle, CheckCircle2, Clock, Mail } from 'lucide-react';
import type React from 'react';

/**
 * Infrastructure reads in the camp's green, the programme in Cevi blue, material in plain grey.
 * Not in Cevi red, which the dashboard keeps for what is overdue.
 */
export const AREA_TEXT_CLASS: Record<HofDashboardArea, string> = {
  infrastructure: 'text-conveniat-green',
  program: 'text-cevi-blue',
  material: 'text-gray-800'
};

/** The area's colour as a fill, for progress bars and dots. */
export const AREA_DOT_CLASS: Record<HofDashboardArea, string> = {
  infrastructure: 'bg-conveniat-green',
  program: 'bg-cevi-blue',
  material: 'bg-gray-500'
};

/** The main action, the submit button of the site's forms. */
export const PRIMARY_BUTTON_CLASS = 'bg-conveniat-green inline-flex min-h-10 cursor-pointer items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-bold text-gray-100 transition duration-100 hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50 sm:text-base [&_svg]:h-4 [&_svg]:w-4';

/** A secondary action, the back button of the site's forms. */
export const SECONDARY_BUTTON_CLASS = 'inline-flex min-h-10 cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-gray-500 px-4 py-2 text-sm font-semibold text-gray-500 transition duration-100 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50 sm:text-base [&_svg]:h-4 [&_svg]:w-4';

/** The card every part of the dashboard sits in, so their edges and padding match. */
export const Panel: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = t0 => {
  const $ = _c(5);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("rounded-xl border border-gray-100 bg-white p-5 shadow-sm @xl:p-6", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children || $[3] !== t1) {
    t2 = <section className={t1}>{children}</section>;
    $[2] = children;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};

/** The heading of a panel, in the area's colour or the camp's green. */
export const SectionHeading: React.FC<{
  children: React.ReactNode;
  area?: HofDashboardArea;
  className?: string;
}> = t0 => {
  const $ = _c(6);
  const {
    children,
    area,
    className
  } = t0;
  const t1 = area === undefined ? "text-conveniat-green" : AREA_TEXT_CLASS[area];
  let t2;
  if ($[0] !== className || $[1] !== t1) {
    t2 = cn("font-heading text-base font-extrabold text-balance", t1, className);
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== children || $[4] !== t2) {
    t3 = <h3 className={t2}>{children}</h3>;
    $[3] = children;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};

/**
 * How loud a status reads: done, open, due soon, sent back for revision or overdue, as the
 * spec's traffic light.
 */
type StatusTone = 'done' | 'neutral' | 'warning' | 'attention' | 'alert';
const TONE_STYLE: Record<StatusTone, {
  icon: React.FC<{
    className?: string;
  }>;
  className: string;
}> = {
  done: {
    icon: CheckCircle2,
    className: 'bg-emerald-50 text-emerald-800 ring-emerald-200'
  },
  neutral: {
    icon: Clock,
    className: 'bg-gray-100 text-gray-700 ring-gray-200'
  },
  warning: {
    icon: Clock,
    className: 'bg-amber-50 text-amber-800 ring-amber-200'
  },
  // the Hof has to act: amber like due soon, marked like an alert
  attention: {
    icon: AlertCircle,
    className: 'bg-amber-50 text-amber-800 ring-amber-200'
  },
  alert: {
    icon: AlertCircle,
    className: 'bg-red-50 text-red-800 ring-red-200'
  }
};

/** A short status with its icon, tinted by how urgent it is. */
export const StatusPill: React.FC<{
  tone: StatusTone;
  children: React.ReactNode;
}> = t0 => {
  const $ = _c(8);
  const {
    tone,
    children
  } = t0;
  const {
    icon: Icon,
    className
  } = TONE_STYLE[tone];
  let t1;
  if ($[0] !== className) {
    t1 = cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== Icon) {
    t2 = <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden={true} />;
    $[2] = Icon;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== children || $[5] !== t1 || $[6] !== t2) {
    t3 = <span className={t1}>{t2}{children}</span>;
    $[4] = children;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
};
const STATE_TONE: Record<SubmissionState, StatusTone> = {
  done: 'done',
  open: 'neutral',
  dueSoon: 'warning',
  overdue: 'alert',
  closed: 'neutral'
};

/** The Ressort's status once handed in, else that it is still missing. */
const progressLabel = (progress: SubmissionProgress, locale: Locale): string => {
  if (progress.state === 'closed') return translate('gapClosed', locale);
  if (progress.gap === 'missing') return translate('gapMissing', locale);
  if (progress.gap === 'revision') return HOF_ENTRY_STATUS_LABELS.revisionRequired[locale];
  return HOF_ENTRY_STATUS_LABELS[progress.status ?? 'submitted'][locale];
};

/** Where a form stands, as a pill: the same wherever it is shown. */
export const ProgressPill: React.FC<{
  progress: SubmissionProgress;
  locale: Locale;
}> = t0 => {
  const $ = _c(6);
  const {
    progress,
    locale
  } = t0;
  const t1 = progress.gap === "revision" && progress.state !== "overdue" ? "attention" : STATE_TONE[progress.state];
  let t2;
  if ($[0] !== locale || $[1] !== progress) {
    t2 = progressLabel(progress, locale);
    $[0] = locale;
    $[1] = progress;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1 || $[4] !== t2) {
    t3 = <StatusPill tone={t1}>{t2}</StatusPill>;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
};

/**
 * Where a form stands, named as everywhere else, and while something is missing the due date
 * and how far off it is.
 */
export const ProgressLine: React.FC<{
  progress: SubmissionProgress;
  locale: Locale;
}> = t0 => {
  const $ = _c(17);
  const {
    progress,
    locale
  } = t0;
  let due;
  if ($[0] !== locale || $[1] !== progress) {
    due = [];
    if (isOpen(progress) && progress.deadline !== undefined) {
      let t1;
      if ($[3] !== locale || $[4] !== progress.deadline) {
        t1 = translate("dueOn", locale, {
          date: formatDate(progress.deadline, locale)
        });
        $[3] = locale;
        $[4] = progress.deadline;
        $[5] = t1;
      } else {
        t1 = $[5];
      }
      due.push(t1);
    }
    if (isOpen(progress) && progress.daysLeft !== undefined) {
      let t1;
      if ($[6] !== locale || $[7] !== progress.daysLeft) {
        t1 = formatCountdown(progress.daysLeft, locale);
        $[6] = locale;
        $[7] = progress.daysLeft;
        $[8] = t1;
      } else {
        t1 = $[8];
      }
      due.push(t1);
    }
    $[0] = locale;
    $[1] = progress;
    $[2] = due;
  } else {
    due = $[2];
  }
  let t1;
  if ($[9] !== locale || $[10] !== progress) {
    t1 = <ProgressPill progress={progress} locale={locale} />;
    $[9] = locale;
    $[10] = progress;
    $[11] = t1;
  } else {
    t1 = $[11];
  }
  let t2;
  if ($[12] !== due) {
    t2 = due.length > 0 && <span className="text-gray-600">{due.join(" \xB7 ")}</span>;
    $[12] = due;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  let t3;
  if ($[14] !== t1 || $[15] !== t2) {
    t3 = <span className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">{t1}{t2}</span>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t3;
  } else {
    t3 = $[16];
  }
  return t3;
};

/**
 * The Hof's responsible people (AVP), as Cevi.DB knows them: their name, and the address as a
 * link big enough to tap.
 */
export const ResponsibleList: React.FC<{
  people: HofContact[];
  locale: Locale;
}> = t0 => {
  const $ = _c(3);
  const {
    people,
    locale
  } = t0;
  let t1;
  if ($[0] !== locale || $[1] !== people) {
    t1 = people.length === 0 ? <p className="text-sm text-gray-500">{translate("responsibleMissing", locale)}</p> : <ul className="grid gap-4 @2xl:grid-cols-2 @5xl:grid-cols-3">{people.map(_temp)}</ul>;
    $[0] = locale;
    $[1] = people;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

/** The share of submissions handed in, labelled for screen readers with its count. */
export const ProgressBar: React.FC<{
  percent: number;
  area: HofDashboardArea;
  label: string;
}> = t0 => {
  const $ = _c(11);
  const {
    percent,
    area,
    label
  } = t0;
  const t1 = AREA_DOT_CLASS[area];
  let t2;
  if ($[0] !== t1) {
    t2 = cn("h-full rounded-full", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `${percent}%`;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      width: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t2 || $[5] !== t4) {
    t5 = <div className={t2} style={t4} />;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== label || $[8] !== percent || $[9] !== t5) {
    t6 = <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-100" role="progressbar" aria-label={label} aria-valuenow={percent} aria-valuemin={0} aria-valuemax={100}>{t5}</div>;
    $[7] = label;
    $[8] = percent;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
function _temp(person) {
  return <li key={person.email} className="text-sm text-gray-900">{person.name !== undefined && <p className="font-semibold">{person.name}</p>}<a href={`mailto:${person.email}`} className="text-conveniat-green flex min-h-11 items-center gap-1.5 break-all hover:underline"><Mail className="h-4 w-4 shrink-0" aria-hidden={true} />{person.email}</a></li>;
}